import React, { useEffect, useRef } from "react";
import Resources from "../../types/Resources";
import Button from "../Common/Button";
import Multiselect from "multiselect-react-dropdown";
import Department from "../../types/Department";
import { CodeStatus, HarbourOffice, PermitCategory, PermitTypeFilter } from "../../common/enums";
import LabelCheckbox from "../Common/LabelCheckbox";
import { Dropdown } from "react-bootstrap";
import WorkPermitFilter from "../../types/filters/WorkPermitFilter";

type WorkPermitFilterDropdownProps = {
    filter: WorkPermitFilter;
    resources: Resources;
    allDepartmentOptions: Department[];
    allStatusOptions: CodeStatus[];
    allPermitCategoryOptions: PermitCategory[];
    allHarbourOfficeOptions: HarbourOffice[];
    allEnergyHarbourPermitTypeOptions: PermitTypeFilter[];
    allPortControlPermitTypeOptions: PermitTypeFilter[];
    onApplyFilter: (options?: { fromDate?: Date; toDate?: Date }) => void;
    onSelectedDepartmentsChange: (selectedOptions: Department[]) => void;
    onCheckboxChange: (key: keyof WorkPermitFilter, value: any, checked: boolean) => void;
};

function WorkPermitFilterDropdown({
    filter,
    resources,
    allDepartmentOptions,
    allStatusOptions,
    allPermitCategoryOptions,
    allEnergyHarbourPermitTypeOptions,
    allPortControlPermitTypeOptions,
    allHarbourOfficeOptions,
    onApplyFilter,
    onSelectedDepartmentsChange,
    onCheckboxChange
}: WorkPermitFilterDropdownProps) {
    const selectedDepartmentOptions = allDepartmentOptions.filter((d) => filter.departmentIds.includes(d.id));

    return (
        <Dropdown>
            <Dropdown.Toggle style={{ backgroundColor: "#0074ba", width: "11rem" }}>
                <i className="fas fa-filter me-1"></i>
                Filter
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-2" style={{ width: "50vw", maxWidth: "1000px", minWidth: "600px" }}>
                <div className="d-flex align-items-center border-bottom pt-2 pb-2">
                    <h1 className="h6 me-1">{resources.get("Department")}</h1>
                    <div className="w-100">
                        <Multiselect
                            displayValue="name"
                            placeholder={resources.get("ChooseDepartments")}
                            options={allDepartmentOptions}
                            selectedValues={selectedDepartmentOptions}
                            onSelect={onSelectedDepartmentsChange}
                            onRemove={onSelectedDepartmentsChange}
                            hidePlaceholder={true}
                        />
                    </div>
                </div>

                <div className="d-flex border-bottom pt-2 pb-2">
                    <h6 className="me-3">Status</h6>
                    <div className="d-flex flex-wrap">
                        {allStatusOptions.map((codeStatusOption) => (
                            <LabelCheckbox
                                checked={filter.statuses.includes(codeStatusOption)}
                                id={`work-permit-filter__status-option-${codeStatusOption}`}
                                label={resources.get(codeStatusOption)}
                                key={codeStatusOption}
                                onChange={(checked) => onCheckboxChange("statuses", codeStatusOption, checked)}
                                containerClassName="me-3"
                                readOnly={false}
                            />
                        ))}
                    </div>
                </div>

                <div className="d-flex border-bottom pt-2 pb-2">
                    <h6 className="me-3">{resources.get("Category")}</h6>
                    <div className="d-flex flex-wrap">
                        {allPermitCategoryOptions.map((permitCategory) => (
                            <LabelCheckbox
                                checked={filter.categories.includes(permitCategory)}
                                id={`work-permit-filter__category-option-${permitCategory}`}
                                label={resources.get(permitCategory)}
                                key={permitCategory}
                                onChange={(checked) => onCheckboxChange("categories", permitCategory, checked)}
                                containerClassName="me-3"
                                readOnly={false}
                            />
                        ))}
                    </div>
                </div>

                <div className="d-flex border-bottom pt-2 pb-2">
                    <h6 className="me-3">{resources.get("EnergyHarbour")}</h6>
                    <div className="d-flex flex-wrap">
                        {allEnergyHarbourPermitTypeOptions.map((permitType) => (
                            <LabelCheckbox
                                checked={filter.permitTypes.includes(permitType)}
                                id={`work-permit-filter__permit-type-option-${permitType}`}
                                label={resources.get(permitType)}
                                key={permitType}
                                onChange={(checked) => onCheckboxChange("permitTypes", permitType, checked)}
                                containerClassName="me-3"
                                readOnly={false}
                            />
                        ))}
                        {allHarbourOfficeOptions.map((harbourOffice) => (
                            <LabelCheckbox
                                checked={filter.harbourOffices.includes(harbourOffice)}
                                id={`work-permit-filter__harbour-office-option-${harbourOffice}`}
                                label={harbourOffice}
                                key={harbourOffice}
                                onChange={(checked) => onCheckboxChange("harbourOffices", harbourOffice, checked)}
                                containerClassName="me-3"
                                readOnly={false}
                            />
                        ))}
                    </div>
                </div>
                <div className="d-flex border-bottom pt-2 pb-2">
                    <h6 className="me-3">HMO</h6>
                    <div className="d-flex flex-wrap">
                        {allPortControlPermitTypeOptions.map((permitType) => (
                            <LabelCheckbox
                                checked={filter.permitTypes.includes(permitType)}
                                id={`work-permit-filter__permit-type-option-${permitType}`}
                                label={resources.get(permitType)}
                                key={permitType}
                                onChange={(checked) => onCheckboxChange("permitTypes", permitType, checked)}
                                containerClassName="me-3"
                                readOnly={false}
                            />
                        ))}
                    </div>
                </div>
                <Button className="pt-2 pb-2 mt-2" onClick={onApplyFilter}>
                    {resources.get("ApplyFilter")}
                </Button>
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default WorkPermitFilterDropdown;
